import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, ArrowRight, Plus, MessageCircle } from "lucide-react";

export default function PastorDashboard() {
  const { user } = useAuth();
  const [couples, setCouples] = useState([]);
  const [unread, setUnread] = useState({});
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [showJoin, setShowJoin] = useState(false);
  const [joining, setJoining] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user && user.account_type !== "pastor") {
      nav("/dashboard");
      return;
    }
    load();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, [user]);

  const loadUnread = () => {
    api.get("/pastor/unread").then(({ data }) => setUnread(data || {})).catch(() => {});
  };

  const load = () => {
    api
      .get("/pastor/couples")
      .then(({ data }) => {
        setCouples(Array.isArray(data) ? data : []);
      })
      .catch(() => toast.error("Impossible de charger vos couples."))
      .finally(() => setLoading(false));
    loadUnread();
  };

  const joinCouple = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      await api.post("/pastor/join", { pastor_code: code });
      toast.success("Couple ajouté à votre suivi.");
      setCode("");
      setShowJoin(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="flex items-start justify-between mb-12 flex-wrap gap-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
            Espace conseiller
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            Bienvenue <span className="italic">{user?.name}</span>
          </h1>
          <p className="mt-4 text-[#5C5754]">
            {couples.length === 0
              ? "Vous n'accompagnez aucun couple pour l'instant."
              : `Vous accompagnez ${couples.length} couple${couples.length > 1 ? "s" : ""}.`}
          </p>
        </div>
        <button
          data-testid="pastor-add-couple-toggle"
          onClick={() => setShowJoin((v) => !v)}
          className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-6 py-3 transition-colors inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Ajouter un couple
        </button>
      </div>

      {showJoin && (
        <form
          onSubmit={joinCouple}
          className="mb-12 bg-white border border-[#E6E2DD] rounded-2xl p-6 md:p-8 animate-fade-in-up"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold mb-3">
            Code pasteur communiqué par le couple
          </p>
          <div className="flex gap-3 flex-wrap">
            <input
              data-testid="pastor-code-input"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="flex-1 min-w-[200px] bg-[#FAF9F6] border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3 text-xl tracking-[0.15em] text-center font-heading"
              placeholder="XXXXXXXX"
              maxLength={10}
            />
            <button
              data-testid="pastor-join-submit"
              type="submit"
              disabled={joining}
              className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-xl px-6 py-3 disabled:opacity-50"
            >
              {joining ? "…" : "Rejoindre"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-[#8A8480]">Chargement…</p>
      ) : couples.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E6E2DD] rounded-3xl">
          <Users className="w-8 h-8 text-[#8A8480] mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-heading text-2xl mb-2">Aucun couple suivi</p>
          <p className="text-[#5C5754] mb-6">
            Demandez au couple son code pasteur puis ajoutez-le ici.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {couples.map((c) => (
            <Link
              key={c.id}
              to={`/pastor/couples/${c.id}`}
              data-testid={`pastor-couple-card-${c.id}`}
              className="group bg-white border border-[#E6E2DD] rounded-2xl p-6 hover:border-[#C86B5E]/40 hover:shadow-lg transition-all relative"
            >
              {unread[c.id] > 0 && (
                <span
                  data-testid={`unread-badge-${c.id}`}
                  className="absolute top-4 right-4 inline-flex items-center gap-1 bg-[#C86B5E] text-white text-xs font-semibold rounded-full px-2.5 py-1"
                >
                  <MessageCircle className="w-3 h-3" /> {unread[c.id]}
                </span>
              )}
              <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-3">
                {c.is_solo ? "Célibataire · auto-analyse" : "Couple"}
              </p>
              <p className="font-heading text-2xl leading-snug mb-1">
                {c.partner_a?.name}
                {!c.is_solo && c.partner_b && (
                  <span className="text-[#8A8480] font-light"> & </span>
                )}
                {!c.is_solo &&
                  (c.partner_b?.name || <span className="text-[#8A8480] italic text-lg">(en attente)</span>)}
              </p>
              {c.couple_name && (
                <p className="text-sm text-[#5C5754] italic mb-4">{c.couple_name}</p>
              )}
              <div className="flex items-center justify-between pt-4 border-t border-[#F3EFEB] text-sm text-[#5C5754]">
                <span>
                  {c.analyses_count} analyse{c.analyses_count !== 1 ? "s" : ""}
                </span>
                <ArrowRight className="w-4 h-4 text-[#8A8480] group-hover:text-[#C86B5E] group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
